import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';

export interface ThinkingEntry {
  id: string;
  content: string;
  timestamp: number;
}

export interface PipelineStep {
  id: string;
  name: string;
  status: 'started' | 'completed' | 'failed';
  startedAt: number;
  completedAt?: number;
}

export interface TaskEntry {
  id: string;
  name: string;
  status: 'started' | 'completed' | 'failed';
  startedAt: number;
  completedAt?: number;
}

export interface Warning {
  id: string;
  message: string;
  timestamp: number;
}

interface ThinkingBlockProps {
  blocks: ThinkingEntry[];
  steps: PipelineStep[];
  tasks?: TaskEntry[];
  responsePreview?: string | null;
  warnings?: Warning[];
  isActive: boolean;
  startTime: number | null;
}

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(4px) scale(0.95); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  margin: 0.75rem 0;
  border-left: 2px solid var(--accent-subtle);
  border-radius: 0 8px 8px 0;
  background: var(--bg-surface);
  overflow: hidden;
`;

const Header = styled.button<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--text-muted);
  text-align: left;
  transition: color 0.2s;

  &:hover {
    color: var(--text-secondary);
  }
`;

const ThinkingIcon = styled.span<{ $active: boolean }>`
  font-size: 0.875rem;
  ${({ $active }) => $active && css`animation: ${pulse} 1.5s ease-in-out infinite;`}
`;

const Label = styled.span`
  flex: 1;
`;

const Chevron = styled.span<{ $expanded: boolean }>`
  font-size: 0.625rem;
  transition: transform 0.2s;
  transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
`;

const Content = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '600px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const ContentInner = styled.div`
  padding: 0 1rem 0.75rem;
`;

const ThinkingText = styled.div`
  font-size: 0.8125rem;
  line-height: 1.65;
  color: var(--text-muted);
  white-space: pre-wrap;
  word-break: break-word;

  code {
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 0.75rem;
    padding: 1px 5px;
    background: var(--bg-elevated);
    border-radius: 3px;
    color: var(--text-secondary);
  }
`;

const Cursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1em;
  background: var(--accent);
  margin-left: 1px;
  vertical-align: text-bottom;
  animation: ${blink} 0.8s step-end infinite;
`;

const StepList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.5rem;
`;

const StepBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  padding: 2px 8px;
  border-radius: 4px;
  animation: ${slideIn} 0.3s ease-out both;
  transition: background 0.3s, color 0.3s, box-shadow 0.3s;

  ${({ $status }) =>
    $status === 'completed'
      ? css`
          background: var(--success-subtle, rgba(34, 197, 94, 0.1));
          color: var(--success, #22c55e);
        `
      : $status === 'failed'
        ? css`
            background: var(--error-subtle, rgba(239, 68, 68, 0.1));
            color: var(--error, #ef4444);
          `
        : css`
            background: linear-gradient(
              90deg,
              var(--accent-subtle, rgba(99, 102, 241, 0.08)) 0%,
              rgba(99, 102, 241, 0.18) 50%,
              var(--accent-subtle, rgba(99, 102, 241, 0.08)) 100%
            );
            background-size: 200% 100%;
            animation: ${slideIn} 0.3s ease-out both,
                       ${shimmer} 2s ease-in-out infinite;
            color: var(--accent, #6366f1);
            box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.15);
          `}
`;

const Spinner = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 1.5px solid rgba(99, 102, 241, 0.25);
  border-top-color: var(--accent, #6366f1);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

const ElapsedTimer = styled.span`
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
`;

const TaskBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  padding: 2px 8px;
  border-radius: 4px;
  animation: ${slideIn} 0.3s ease-out both;
  transition: background 0.3s, color 0.3s, box-shadow 0.3s;

  ${({ $status }) =>
    $status === 'completed'
      ? css`
          background: var(--success-subtle, rgba(34, 197, 94, 0.1));
          color: var(--success, #22c55e);
        `
      : $status === 'failed'
        ? css`
            background: var(--error-subtle, rgba(239, 68, 68, 0.1));
            color: var(--error, #ef4444);
          `
        : css`
            background: linear-gradient(
              90deg,
              rgba(245, 158, 11, 0.08) 0%,
              rgba(245, 158, 11, 0.18) 50%,
              rgba(245, 158, 11, 0.08) 100%
            );
            background-size: 200% 100%;
            animation: ${slideIn} 0.3s ease-out both,
                       ${shimmer} 2s ease-in-out infinite;
            color: var(--warning, #f59e0b);
            box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.15);
          `}
`;

const SectionLabel = styled.div`
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-top: 0.625rem;
  margin-bottom: 0.25rem;
`;

const PreviewBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  padding: 0.375rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--success, #22c55e);
  background: var(--success-subtle, rgba(34, 197, 94, 0.06));
  border: 1px solid rgba(34, 197, 94, 0.15);
  animation: ${slideIn} 0.3s ease-out both;
`;

const WarningItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  margin-top: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  line-height: 1.4;
  color: var(--warning, #f59e0b);
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.15);
  animation: ${slideIn} 0.3s ease-out both;
`;

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function renderInlineCode(text: string): React.ReactNode {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i}>{part.slice(1, -1)}</code>;
    }
    return part;
  });
}

function useTickingClock(active: boolean, intervalMs = 500): number {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs]);
  return Date.now();
}

const LiveStep: React.FC<{ step: PipelineStep }> = ({ step }) => {
  const isRunning = step.status === 'started';
  const now = useTickingClock(isRunning);
  const elapsed = isRunning
    ? now - step.startedAt
    : step.completedAt && step.startedAt
      ? step.completedAt - step.startedAt
      : 0;

  return (
    <StepBadge $status={step.status}>
      {step.status === 'completed' ? (
        '✓'
      ) : step.status === 'failed' ? (
        '✕'
      ) : (
        <Spinner />
      )}
      {step.name}
      {elapsed > 0 && (
        <ElapsedTimer>({formatDuration(elapsed)})</ElapsedTimer>
      )}
    </StepBadge>
  );
};

const LiveTask: React.FC<{ task: TaskEntry }> = ({ task }) => {
  const isRunning = task.status === 'started';
  const now = useTickingClock(isRunning);
  const elapsed = isRunning
    ? now - task.startedAt
    : task.completedAt && task.startedAt
      ? task.completedAt - task.startedAt
      : 0;

  return (
    <TaskBadge $status={task.status}>
      {task.status === 'completed' ? (
        '✓'
      ) : task.status === 'failed' ? (
        '✕'
      ) : (
        <Spinner />
      )}
      {task.name}
      {elapsed > 0 && (
        <ElapsedTimer>({formatDuration(elapsed)})</ElapsedTimer>
      )}
    </TaskBadge>
  );
};

const ThinkingBlock: React.FC<ThinkingBlockProps> = ({
  blocks,
  steps,
  tasks = [],
  responsePreview,
  warnings = [],
  isActive,
  startTime,
}) => {
  const [expanded, setExpanded] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const now = useTickingClock(isActive);

  useEffect(() => {
    if (isActive && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [blocks, steps, tasks, warnings, responsePreview, isActive]);

  const elapsed = startTime ? now - startTime : 0;
  const combinedText = blocks.map((b) => b.content).join('');

  const completedCount = steps.filter((s) => s.status === 'completed').length;
  const totalCount = steps.length;
  const completedTaskCount = tasks.filter((t) => t.status === 'completed').length;
  const totalTaskCount = tasks.length;

  const buildLabel = useCallback(() => {
    if (isActive) {
      const parts: string[] = [];
      if (elapsed > 0) parts.push(formatDuration(elapsed));
      if (totalCount > 0) parts.push(`${completedCount}/${totalCount} steps`);
      if (totalTaskCount > 0) parts.push(`${completedTaskCount}/${totalTaskCount} tasks`);
      if (responsePreview) parts.push('Generating response');
      return parts.length > 0
        ? `Thinking... ${parts.join(' \u00B7 ')}`
        : 'Thinking...';
    }
    const suffixes: string[] = [];
    if (completedCount > 0)
      suffixes.push(`${completedCount} step${completedCount > 1 ? 's' : ''}`);
    if (completedTaskCount > 0)
      suffixes.push(`${completedTaskCount} task${completedTaskCount > 1 ? 's' : ''}`);
    const suffix = suffixes.length > 0 ? ` \u00B7 ${suffixes.join(', ')}` : '';
    return `Thought for ${formatDuration(elapsed)}${suffix}`;
  }, [isActive, elapsed, completedCount, totalCount, completedTaskCount, totalTaskCount, responsePreview]);

  if (blocks.length === 0 && steps.length === 0 && tasks.length === 0 && !isActive) return null;

  return (
    <Container>
      <Header $expanded={expanded} onClick={() => setExpanded((p) => !p)}>
        <ThinkingIcon $active={isActive}>&#x2728;</ThinkingIcon>
        <Label>{buildLabel()}</Label>
        <Chevron $expanded={expanded}>&#x25B6;</Chevron>
      </Header>
      <Content $expanded={expanded}>
        <ContentInner ref={contentRef}>
          {combinedText && (
            <ThinkingText>
              {renderInlineCode(combinedText)}
              {isActive && <Cursor />}
            </ThinkingText>
          )}
          {steps.length > 0 && (
            <StepList>
              {steps.map((step) => (
                <LiveStep key={step.id} step={step} />
              ))}
            </StepList>
          )}
          {tasks.length > 0 && (
            <>
              <SectionLabel>Tasks</SectionLabel>
              <StepList>
                {tasks.map((task) => (
                  <LiveTask key={task.id} task={task} />
                ))}
              </StepList>
            </>
          )}
          {warnings.map((w) => (
            <WarningItem key={w.id}>
              <span>&#x26A0;</span> {w.message}
            </WarningItem>
          ))}
          {responsePreview && isActive && (
            <PreviewBanner>
              <span>&#x2713;</span>
              Generating response
            </PreviewBanner>
          )}
        </ContentInner>
      </Content>
    </Container>
  );
};

export default ThinkingBlock;
